import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMunicipality } from "./MunicipalityContext";
import { useConnection } from "./ConnectionContext";

const MUNICIPALITY_LINKS = ['add-municipality', 'view-municipality'];

const MASTERS_R1_MAP: Record<string, string[]> = {
  propertyType:            ['property-type'],
  propertyTypeDescription: ['add-property-type-description', 'property-type-description'],
  useOfProperty:           ['add-use-of-property', 'use-of-property'],
  purposeOfProperty:       ['add-purpose-of-property', 'purpose-of-property'],
  propertyDescription:     ['addpropertydescription', 'propertydescriptionlist'],
  occupancyType:           ['occupancy-type-list'],
  propertyStructure:       ['property-structure-list'],
};

const MASTERS_R2_MAP: Record<string, string[]> = {
  propertyType:            ['add-borough', 'borough-list'],
  propertyTypeDescription: ['add-ward', 'ward-list'],
  propertyStructure:       ['add-location', 'location-list'],
  mouza:                   ['add-mouza', 'mouza-list'],
  useOfProperty:           ['add-street', 'street-list'],
  purposeOfProperty:       ['add-holding', 'holding-list'],
};

const SURVEYER_LINKS  = ['add-surveyer', 'view-surveyer'];
const COLLECTOR_LINKS = ['add-collector', 'view-collector'];
const CASHIER_LINKS   = ['add-cashier', 'view-cashier'];
const CSV_LINKS       = ['csv-upload-status', 'csv-failed-status'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isChildActive = (links: string[], active: string | null) =>
  links.some(l => l === active);

const isSubParentActive = (map: Record<string, string[]>, key: string, active: string | null) =>
  isChildActive(map[key] ?? [], active);

const isParentActive = (map: Record<string, string[]>, active: string | null) =>
  Object.values(map).flat().some(l => l === active);


const topRow = (active: boolean) =>
  `flex items-center gap-x-3 px-4 py-2 cursor-pointer select-none transition-all duration-150 ${
    active
      ? 'bg-[#e7872e] text-white'
      : 'text-white hover:bg-[#e7872e]/80 hover:text-white'
  }`;

/** Level-2 dropdown row */
const midRow = (active: boolean) =>
  `flex items-center gap-x-3 pl-6 pr-3 py-2 cursor-pointer select-none transition-all duration-150 ${
    active
      ? 'bg-[#bf6820] text-white'
      : 'text-gray-200 hover:bg-[#bf6820]/70 hover:text-white'
  }`;

/** Leaf link inside deepest submenu */
const leafLink = (active: boolean) =>
  `block py-2 pl-10 pr-3 text-sm transition-all duration-150 border-l-2 ${
    active
      ? 'text-[#f5a623] font-semibold border-[#f5a623]'
      : 'text-gray-300 hover:text-[#f5a623] border-transparent hover:border-[#f5a623]/50'
  }`;

const subLeafLink = (active: boolean) =>
  `block py-2 pl-8 pr-3 text-sm transition-all duration-150 border-l-2 ${
    active
      ? 'text-[#f5a623] font-semibold border-[#f5a623]'
      : 'text-gray-300 hover:text-[#f5a623] border-transparent hover:border-[#f5a623]/50'
  }`;


export const Sidebar: React.FC<any> = ({
  onLogout,
  roleId,
}) => {
  const [activeLink, setActiveLink] = useState<string | null>(
    () => sessionStorage.getItem("activeLink")
  );

  const [open, setOpen] = useState({
    municipality: false,
    masters:      false,
    surveyer:     false,
    collector:    false,
    cashier:      false,
    csvLog:       false,
  });

  const [subOpen, setSubOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeLink) return;

    if (CSV_LINKS.includes(activeLink))       { setOpen(p => ({ ...p, csvLog: true })); return; }
    if (SURVEYER_LINKS.includes(activeLink))  { setOpen(p => ({ ...p, surveyer: true })); return; }
    if (COLLECTOR_LINKS.includes(activeLink)) { setOpen(p => ({ ...p, collector: true })); return; }
    if (CASHIER_LINKS.includes(activeLink))   { setOpen(p => ({ ...p, cashier: true })); return; }
    if (MUNICIPALITY_LINKS.includes(activeLink)) { setOpen(p => ({ ...p, municipality: true })); return; }

    const masterMap = roleId == 2 ? MASTERS_R2_MAP : MASTERS_R1_MAP;
    for (const [subKey, links] of Object.entries(masterMap)) {
      if (links.includes(activeLink)) {
        setOpen(p => ({ ...p, masters: true }));
        setSubOpen(p => ({ ...p, [subKey]: true }));
        return;
      }
    }
  }, [activeLink, roleId]);

  const toggleTop = (key: keyof typeof open) => {
    setOpen(p => {
      const next = { ...p };
      (Object.keys(next) as (keyof typeof open)[]).forEach(k => {
        next[k] = k === key ? !p[key] : false;
      });
      return next;
    });
    setSubOpen({});
  };

  const toggleSub = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setSubOpen(p => {
      const next: Record<string, boolean> = {};
      Object.keys(p).forEach(k => { next[k] = false; });
      next[key] = !p[key];
      return next;
    });
  };

  const navigate = (e: React.MouseEvent, link: string, keepDropdown = false) => {
    e.stopPropagation();
    setActiveLink(link);
    sessionStorage.setItem("activeLink", link);
    if (!keepDropdown) {
      setOpen({ municipality: false, masters: false, surveyer: false, collector: false, cashier: false, csvLog: false });
      setSubOpen({});
    }
  };

  const { setResetMunicipality } = useMunicipality();
  const Role_tyep = localStorage.getItem('role_type');

  const masterMap = roleId == 2 ? MASTERS_R2_MAP : MASTERS_R1_MAP;

  const csvActive       = isChildActive(CSV_LINKS, activeLink);
  const surveyerActive  = isChildActive(SURVEYER_LINKS, activeLink);
  const collectorActive = isChildActive(COLLECTOR_LINKS, activeLink);
  const cashierActive   = isChildActive(CASHIER_LINKS, activeLink);
  const municiActive    = isChildActive(MUNICIPALITY_LINKS, activeLink);
  const mastersActive   = isParentActive(masterMap, activeLink);

  const renderSubMenu = (
    subKey: string,
    icon: string,
    label: string,
    links: { to: string; key: string; label: string }[]
  ) => {
    const isSubActive = isSubParentActive(masterMap, subKey, activeLink);
    const isSubOpen   = !!subOpen[subKey];
    return (
      <li key={subKey}>
        <div
          className={midRow(isSubActive || isSubOpen)}
          onClick={(e) => toggleSub(e, subKey)}
        >
          <span><i className={icon}></i></span>
          <span className="text-sm flex-1">{label}</span>
          <i className={`${isSubOpen ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
        </div>
        {isSubOpen && (
          <ul className="bg-[#1a2f5a]">
            {links.map(({ to, key, label: lbl }) => (
              <li key={key}>
                <Link
                  to={to}
                  onClick={(e) => navigate(e, key, true)}
                  className={leafLink(activeLink === key)}
                >
                  {lbl}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const { setResetConnection } = useConnection();

  return (
    <nav className="fixed top-0 left-0 h-full z-[2] bg-[#2b437e] text-white dark:bg-gray-800 shadow-lg transition-all duration-300 transform translate-x-0 w-64 sidebar-nav overflow-y-auto overflow-x-hidden">

      {/* Header */}
      <div className="flex gap-3 items-center py-4 px-4 border-b border-white/10 shrink-0 bg-[#162750]">
        <div className="w-10 h-10 shrink-0 flex items-center justify-center">
          <img src="/assets/surveyLogo.png" alt="Logo" className="max-w-full max-h-full object-contain" />
        </div>
        <div>
          <p className="text-xs text-blue-300 font-medium uppercase tracking-wider leading-none mb-1">
            {roleId == 1 ? 'Super Admin' : 'Municipality'}
          </p>
          <div className="h-0.5 w-8 bg-[#e7872e] rounded-full" />
        </div>
      </div>

      <ul className="flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-0.5">
        <li>
          <Link to="/dashboard/overview" onClick={(e) => navigate(e, 'overview')}
            className={topRow(activeLink === 'overview')}>
            <i className="ri-home-smile-fill text-base w-5 text-center shrink-0"></i>
            <span className="nav-name text-sm">Overview</span>
          </Link>
        </li>

        {Role_tyep === "Accountant/ Cashier" && (
          <li>
            <Link to="/dashboard/cashier-report" onClick={(e) => navigate(e, 'cashier-report')}
              className={topRow(activeLink === 'cashier-report')}>
              <i className="ri-file-chart-line text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm">Cashier Report</span>
            </Link>
          </li>
        )}

        {Role_tyep === "Field Collector" && (
          <li>
            <Link to="/dashboard/collector-report" onClick={(e) => navigate(e, 'collector-report')}
              className={topRow(activeLink === 'collector-report')}>
              <i className="ri-file-chart-line text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm">Collector Report</span>
            </Link>
          </li>
        )}

        {roleId == 2 && (
          <li>
            <Link to="/dashboard/edit-profile" onClick={(e) => navigate(e, 'edit-profile')}
              className={topRow(activeLink === 'edit-profile')}>
              <i className="ri-profile-fill text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm">Municipality Profile</span>
            </Link>
          </li>
        )}

        {roleId == 1 && (
          <li>
            <div className={topRow(municiActive || open.municipality)} onClick={() => toggleTop('municipality')}>
              <i className="ri-community-line text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm flex-1">Municipality</span>
              <i className={`${open.municipality ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
            </div>
            {open.municipality && (
              <ul className="bg-[#162750]">
                <li><Link to="/dashboard/add-municipality" onClick={(e) => navigate(e, 'add-municipality', true)} className={subLeafLink(activeLink === 'add-municipality')}>Add Municipality</Link></li>
                <li><Link to="/dashboard/view-municipality" onClick={(e) => { navigate(e, 'view-municipality', true); setResetMunicipality(true); }} className={subLeafLink(activeLink === 'view-municipality')}>View Municipality</Link></li>
              </ul>
            )}
          </li>
        )}

        {roleId == 1 && (
          <li>
            <Link to="/dashboard/surveyor-list" onClick={(e) => navigate(e, 'surveyor-list')}
              className={topRow(activeLink === 'surveyor-list')}>
              <i className="ri-user-2-fill text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm">Surveyor List</span>
            </Link>
          </li>
        )}

        {roleId == 2 && (
          <li>
            <div className={topRow(mastersActive || open.masters)} onClick={() => toggleTop('masters')}>
              <i className="ri-file-text-line text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm flex-1">Municipality Masters</span>
              <i className={`${open.masters ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
            </div>
            {open.masters && (
              <ul className="bg-[#162750]">
                {renderSubMenu('propertyType', 'ri-file-list-3-line', 'Borough', [
                  { to: '/dashboard/add-borough',  key: 'add-borough',  label: 'Add Borough' },
                  { to: '/dashboard/borough-list', key: 'borough-list', label: 'Borough List' },
                ])}
                {renderSubMenu('propertyTypeDescription', 'ri-file-list-3-line', 'Ward', [
                  { to: '/dashboard/add-ward',  key: 'add-ward',  label: 'Add Ward' },
                  { to: '/dashboard/ward-list', key: 'ward-list', label: 'Ward List' },
                ])}
                {renderSubMenu('propertyStructure', 'ri-map-pin-fill', 'Locations', [
                  { to: '/dashboard/add-location',  key: 'add-location',  label: 'Add Location' },
                  { to: '/dashboard/location-list', key: 'location-list', label: 'Location List' },
                ])}
                {renderSubMenu('mouza', 'ri-file-list-3-line', 'Mouza', [
                  { to: '/dashboard/add-mouza',  key: 'add-mouza',  label: 'Add Mouza' },
                  { to: '/dashboard/mouza-list', key: 'mouza-list', label: 'Mouza List' },
                ])}
                {renderSubMenu('useOfProperty', 'ri-road-map-line', 'Street', [
                  { to: '/dashboard/add-street',  key: 'add-street',  label: 'Add Street' },
                  { to: '/dashboard/street-list', key: 'street-list', label: 'Street List' },
                ])}
                {renderSubMenu('purposeOfProperty', 'ri-building-2-line', 'Holding', [
                  { to: '/dashboard/add-holding',  key: 'add-holding',  label: 'Add Holding' },
                  { to: '/dashboard/holding-list', key: 'holding-list', label: 'Holding List' },
                ])}
              </ul>
            )}
          </li>
        )}

        {roleId == 2 && (
          <>
            <li>
              <div className={topRow(surveyerActive || open.surveyer)} onClick={() => toggleTop('surveyer')}>
                <i className="ri-team-fill text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm flex-1">Surveyor</span>
                <i className={`${open.surveyer ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
              </div>
              {open.surveyer && (
                <ul className="bg-[#162750]">
                  <li><Link to="/dashboard/add-surveyer" onClick={(e) => navigate(e, 'add-surveyer', true)} className={subLeafLink(activeLink === 'add-surveyer')}>Add Surveyor</Link></li>
                  <li><Link to="/dashboard/view-surveyer" onClick={(e) => navigate(e, 'view-surveyer', true)} className={subLeafLink(activeLink === 'view-surveyer')}>View Surveyor</Link></li>
                </ul>
              )}
            </li>
            <li>
              <div className={topRow(collectorActive || open.collector)} onClick={() => toggleTop('collector')}>
                <i className="ri-user-star-fill text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm flex-1">Collector</span>
                <i className={`${open.collector ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
              </div>
              {open.collector && (
                <ul className="bg-[#162750]">
                  <li><Link to="/dashboard/add-collector" onClick={(e) => navigate(e, 'add-collector', true)} className={subLeafLink(activeLink === 'add-collector')}>Add Collector</Link></li>
                  <li><Link to="/dashboard/view-collector" onClick={(e) => navigate(e, 'view-collector', true)} className={subLeafLink(activeLink === 'view-collector')}>View Collector</Link></li>
                </ul>
              )}
            </li>
            <li>
              <div className={topRow(cashierActive || open.cashier)} onClick={() => toggleTop('cashier')}>
                <i className="ri-user-star-fill text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm flex-1">Cashier/Accountant</span>
                <i className={`${open.cashier ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
              </div>
              {open.cashier && (
                <ul className="bg-[#162750]">
                  <li><Link to="/dashboard/add-cashier" onClick={(e) => navigate(e, 'add-cashier', true)} className={subLeafLink(activeLink === 'add-cashier')}>Add Cashier</Link></li>
                  <li><Link to="/dashboard/view-cashier" onClick={(e) => navigate(e, 'view-cashier', true)} className={subLeafLink(activeLink === 'view-cashier')}>View Cashier</Link></li>
                </ul>
              )}
            </li>
          </>
        )}

        {/* Masters (roleId 1) */}
        {roleId == 1 && (
          <li>
            <div className={topRow(mastersActive || open.masters)} onClick={() => toggleTop('masters')}>
              <i className="ri-file-text-line text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm flex-1">Masters</span>
              <i className={`${open.masters ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
            </div>
            {open.masters && (
              <ul className="bg-[#162750]">
                {renderSubMenu('propertyType', 'ri-file-list-3-line', 'Property Type', [
                  { to: '/dashboard/property-type', key: 'property-type', label: 'Property Type List' },
                ])}
                {renderSubMenu('propertyTypeDescription', 'ri-file-list-3-line', 'Property Type Description', [
                  { to: '/dashboard/add-property-type-description', key: 'add-property-type-description', label: 'Add Property Type Description' },
                  { to: '/dashboard/property-type-description',     key: 'property-type-description',     label: 'Property Type Description List' },
                ])}
                {renderSubMenu('useOfProperty', 'ri-file-list-3-line', 'Use Of Property', [
                  { to: '/dashboard/add-use-of-property', key: 'add-use-of-property', label: 'Add Use Of Property' },
                  { to: '/dashboard/use-of-property',     key: 'use-of-property',     label: 'Use Of Property List' },
                ])}
                {renderSubMenu('purposeOfProperty', 'ri-file-list-3-line', 'Purpose Of Property', [
                  { to: '/dashboard/add-purpose-of-property', key: 'add-purpose-of-property', label: 'Add Purpose Of Property' },
                  { to: '/dashboard/purpose-of-property',     key: 'purpose-of-property',     label: 'Purpose Of Property List' },
                ])}
                {renderSubMenu('propertyDescription', 'ri-file-list-3-line', 'Property Description', [
                  { to: '/dashboard/addpropertydescription',  key: 'addpropertydescription',  label: 'Add Property Description' },
                  { to: '/dashboard/propertydescriptionlist', key: 'propertydescriptionlist', label: 'Property Description List' },
                ])}
                {renderSubMenu('occupancyType', 'ri-file-list-3-line', 'Occupancy Type', [
                  { to: '/dashboard/occupancy-type-list', key: 'occupancy-type-list', label: 'Occupancy Type List' },
                ])}
                {renderSubMenu('propertyStructure', 'ri-file-list-3-line', 'Property Structure', [
                  { to: '/dashboard/property-structure-list', key: 'property-structure-list', label: 'Property Structure List' },
                ])}
              </ul>
            )}
          </li>
        )}

        {roleId == 1 && (
          <>
            <li>
              <Link to="/dashboard/admin-collector" onClick={(e) => navigate(e, 'admin-collector')}
                className={topRow(activeLink === 'admin-collector')}>
                <i className="ri-user-search-line text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Collector</span>
              </Link>
            </li>
            <li>
              <Link to="/dashboard/admin-accountant" onClick={(e) => navigate(e, 'admin-accountant')}
                className={topRow(activeLink === 'admin-accountant')}>
                <i className="ri-calculator-line text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Accountant</span>
              </Link>
            </li>
          
            <li>
              <Link to="/dashboard/reports" onClick={(e) => navigate(e, 'reports')}
                className={topRow(activeLink === 'reports')}>
                <i className="ri-file-chart-line text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Reports</span>
              </Link>
            </li>
                <li>
              <Link to="/dashboard/demand-draft" onClick={(e) => navigate(e, 'demand-draft')} 
              className={topRow(activeLink === 'demand-draft')}
              >
                <i className="ri-file-pdf-fill text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Demand Draft</span>
              </Link>
            </li>
          </>
        )}

        {roleId == 2 && (
          <>
            <li>
              <Link to="/dashboard/surveyor-reports" onClick={(e) => navigate(e, 'surveyor-reports')}
                className={topRow(activeLink === 'surveyor-reports')}>
                <i className="ri-file-chart-line text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Surveyor Reports</span>
              </Link>
            </li>
            <li>
              <Link to="/dashboard/admin-connections" onClick={(e) => { 
  navigate(e, 'admin-connections'); 
  setResetConnection(true); 
}}
                className={topRow(activeLink === 'admin-connections')}>
                <i className="ri-home-wifi-fill text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Connection</span>
              </Link>
            </li>
            <li>
              <Link to="/dashboard/customer-ledger" onClick={(e) => navigate(e, 'customer-leger')}
                className={topRow(activeLink === 'customer-leger')}>
                <i className="ri-hand-coin-line text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Customer Ledger</span>
              </Link>
            </li>
            <li>
              <Link to="/dashboard/demand-draft" onClick={(e) => navigate(e, 'demand-draft')} 
              className={topRow(activeLink === 'demand-draft')}
              >
                <i className="ri-file-pdf-fill text-base w-5 text-center shrink-0"></i>
                <span className="nav-name text-sm">Demand Draft</span>
              </Link>
            </li>
          </>
        )}

        {/* CSV Log */}
        {(roleId == 1 || roleId == 2) && (
          <li>
            <div className={topRow(csvActive || open.csvLog)} onClick={() => toggleTop('csvLog')}>
              <i className="ri-file-upload-line text-base w-5 text-center shrink-0"></i>
              <span className="nav-name text-sm flex-1">CSV Log</span>
              <i className={`${open.csvLog ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} text-base`} />
            </div>
            {open.csvLog && (
              <ul className="bg-[#162750]">
                <li><Link to="/dashboard/csv-upload-status" onClick={(e) => navigate(e, 'csv-upload-status', true)} className={subLeafLink(activeLink === 'csv-upload-status')}>Upload Status</Link></li>
                <li><Link to="/dashboard/csv-failed-status" onClick={(e) => navigate(e, 'csv-failed-status', true)} className={subLeafLink(activeLink === 'csv-failed-status')}>Failed Status</Link></li>
              </ul>
            )}
          </li>
        )}

        {/* Logout */}
        <li className="mt-2 border-t border-white/10 pt-2">
          <Link to="#" onClick={onLogout}
            className="flex items-center gap-x-3 px-4 py-2 text-gray-300 hover:bg-red-600/80 hover:text-white transition-all duration-150 cursor-pointer">
            <i className="ri-logout-box-line text-base w-5 text-center shrink-0"></i>
            <span className="nav-name text-sm">Logout</span>
          </Link>
        </li>

      </ul>
    </nav>
  );
};